import { Children, cloneElement, useRef, useState } from 'react'
import { MultiStepMobileWizardProps, StepProps } from './Types'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { Formik } from 'formik'
import { WizardProvider } from 'components/Wizards/Wizard/WizardContext'
import { Step } from './Step'

import { FieldSchema } from 'components/Wizards/Wizard/Form/Types'
import { useFinishedRenderingNativeMessage } from 'hooks/useFinishedRenderingNativeMessage'

const MultiStepMobileWizardRoot = <Values,>({
  schema,
  onClose,
  onSubmitStep,
  initialValues = {},
  initialFormStatus,
  children,
  initialStep = 0,
  onStepMount,
  isScrolled = false,
  renderHeader = true,
}: MultiStepMobileWizardProps<Values>) => {
  const {
    meta: { title, icon, type },
    steps,
  } = schema

  useFinishedRenderingNativeMessage()

  const [currentStep, _setCurrentStep] = useState(initialStep)
  const backStackRef = useRef<number[]>([])
  const [values, setValues] = useState<Values>(initialValues)
  const [backHandler, setBackHandler] = useState<() => void>()

  const childrenArray = Children.toArray(children)
  const stepSchema = steps[currentStep]

  const setCurrentStep = async (step: number) => {
    if (step !== currentStep) {
      backStackRef.current.push(currentStep)
      await onStepMount?.({ ...stepProps, currentStep: step })
      _setCurrentStep(step)
    }
  }

  const getSchemaField = (fieldName: string) => {
    return Object.entries(stepSchema.fields).find(([name]) => fieldName === name)?.[1] as FieldSchema | undefined
  }

  const clearFields = (fields: (keyof Values)[]) => {
    setValues((vals) => {
      const newVals = { ...vals }
      fields.forEach((field) => delete newVals[field])
      return newVals
    })
  }

  const handleBack = () => {
    if (backHandler) {
      backHandler()
      return
    }

    const nextStep = backStackRef.current.pop()
    if (nextStep === undefined) {
      onClose()
    } else {
      _setCurrentStep(nextStep)
    }
  }

  const stepProps = {
    initialValues,
    initialFormStatus,
    clearFields,
    schema: stepSchema,
    getSchemaField,
    currentStep,
    stepIndex: currentStep,
    totalSteps: steps.length,
    advance: () => setCurrentStep(currentStep + 1),
    setCurrentStep,
    setBackHandler,
    goBack: handleBack,
  }

  const handleSubmit = async (partialValues?: Partial<Values>) => {
    const allValues = { ...values, ...(partialValues ?? {}) }
    setValues(allValues)
    onSubmitStep({ ...stepProps, values: allValues })
  }

  const renderStep = () => {
    if (!stepSchema) {
      throw new Error(`No step schema defined for step ${currentStep}`)
    }

    const formInitialValues = {
      ...values,
      ...(childrenArray.length > 0
        ? (childrenArray[currentStep] as React.ReactElement<StepProps<Values>>).props.initialValues
        : {}),
    }

    return (
      <Formik
        key={currentStep}
        validationSchema={stepSchema}
        onSubmit={handleSubmit}
        initialValues={stepSchema.cast({ ...stepSchema.getDefault(), ...formInitialValues }, { assert: false })}
      >
        {({ submitForm }) => {
          return (
            <div
              aria-current="step"
              data-testid={`wizard-step-${currentStep + 1}`}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
            >
              {childrenArray.length > 0 &&
                cloneElement(childrenArray[currentStep] as React.ReactElement<StepProps<Values>>, {
                  ...stepProps,
                  initialValues: formInitialValues,
                  submit: () => setTimeout(submitForm),
                })}
              {childrenArray.length === 0 && (
                <Step {...stepProps} initialValues={formInitialValues} submit={() => setTimeout(submitForm)} />
              )}
            </div>
          )
        }}
      </Formik>
    )
  }

  return (
    <WizardProvider schema={schema}>
      <MobileWizard.Root>
        {renderHeader && (
          <MobileWizard.Header
            type={type}
            title={title}
            icon={icon}
            onClose={onClose}
            onBack={handleBack}
            isScrolled={isScrolled}
          />
        )}
        {renderStep()}
      </MobileWizard.Root>
    </WizardProvider>
  )
}

const MultiStepMobileWizardComponent = Object.assign(
  <Values,>(props: MultiStepMobileWizardProps<Values>) => <MultiStepMobileWizardRoot {...props} />,
  {
    Root: MultiStepMobileWizardRoot,
    Step: <Values extends object = {}>(props: Partial<StepProps<Values>>) => <Step {...props} />,
  },
)

export const MultiStepMobileWizard = MultiStepMobileWizardComponent
